import React from "react";
import { createPortal } from "react-dom";
import AppIcon from "./AppIcon";
import { PASSWORD_POLICY_MESSAGE } from "../utils/password";

let activeModalCount = 0;
let previousBodyOverflow = "";
let previousBodyPaddingRight = "";
let previousHtmlOverflow = "";

function lockViewportScroll() {
  if (typeof document === "undefined") return;
  activeModalCount += 1;
  if (activeModalCount > 1) return;

  const { body, documentElement } = document;
  previousBodyOverflow = body.style.overflow;
  previousBodyPaddingRight = body.style.paddingRight;
  previousHtmlOverflow = documentElement.style.overflow;

  const scrollbarWidth = Math.max(0, window.innerWidth - documentElement.clientWidth);
  body.classList.add("modal-open");
  documentElement.classList.add("modal-open");
  body.style.overflow = "hidden";
  documentElement.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }
}

function unlockViewportScroll() {
  if (typeof document === "undefined") return;
  activeModalCount = Math.max(0, activeModalCount - 1);
  if (activeModalCount > 0) return;

  const { body, documentElement } = document;
  body.classList.remove("modal-open");
  documentElement.classList.remove("modal-open");
  body.style.overflow = previousBodyOverflow;
  body.style.paddingRight = previousBodyPaddingRight;
  documentElement.style.overflow = previousHtmlOverflow;
}

function useModalLifecycle(open, onClose) {
  React.useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    lockViewportScroll();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      unlockViewportScroll();
    };
  }, [open, onClose]);
}

function ModalPortal({ open, children }) {
  if (!open || typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

export function AlertModal({ open, title = "Notice", message = "", onClose }) {
  return (
    <ModalShell
      open={open}
      size="sm"
      title={title}
      subtitle={null}
      onClose={onClose}
      actions={<button className="btn btn-primary" onClick={onClose} type="button">OK</button>}
    >
      <div className="modal-copy-block" style={{ whiteSpace: "pre-wrap" }}>{message}</div>
    </ModalShell>
  );
}

export function ModalShell({
  open,
  title,
  subtitle,
  children,
  onClose,
  actions,
  size = "md",
  className = "",
  closeOnBackdrop = true,
}) {
  useModalLifecycle(open, onClose);

  const handleStageMouseDown = (event) => {
    if (!closeOnBackdrop || !onClose) return;
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalPortal open={open}>
      <div className="modal-backdrop" role="presentation">
        <div className="modal-stage" onMouseDown={handleStageMouseDown}>
          <div
            className={`modal-card modal-card--${size} ${className}`.trim()}
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3>{title}</h3>
                {subtitle ? <p className="modal-subtitle">{subtitle}</p> : null}
              </div>
              {onClose ? (
                <button className="icon-btn modal-close-btn" type="button" onClick={onClose} aria-label="Close modal">
                  <AppIcon name="close" />
                </button>
              ) : null}
            </div>
            <div className="modal-body">{children}</div>
            {actions ? <div className="modal-actions">{actions}</div> : null}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export function ReqLabel({ children }) {
  return (
    <label>
      {children} <span className="required-mark">*</span>
    </label>
  );
}



export function PasswordCriteriaInfo({ className = "" }) {
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState({ left: 12, top: 12, width: 280 });
  const wrapRef = React.useRef(null);
  const tooltipRef = React.useRef(null);
  const tooltipIdRef = React.useRef(`password-policy-${Math.random().toString(36).slice(2)}`);

  const updatePosition = React.useCallback(() => {
    if (typeof window === "undefined") return;
    const anchor = wrapRef.current?.getBoundingClientRect();
    if (!anchor) return;

    const viewportGap = 12;
    const preferredWidth = 320;
    const minWidth = Math.min(240, Math.max(0, window.innerWidth - viewportGap * 2));
    const width = Math.min(preferredWidth, Math.max(minWidth, window.innerWidth - viewportGap * 2));
    const tooltipHeight = tooltipRef.current?.offsetHeight || 104;
    const anchorCenter = anchor.left + anchor.width / 2;
    const left = Math.min(
      window.innerWidth - width - viewportGap,
      Math.max(viewportGap, anchorCenter - width / 2)
    );
    const belowTop = anchor.bottom + 10;
    const aboveTop = anchor.top - tooltipHeight - 10;
    const top = belowTop + tooltipHeight <= window.innerHeight - viewportGap
      ? belowTop
      : Math.max(viewportGap, aboveTop);

    setPosition({ left, top, width });
  }, []);

  React.useLayoutEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(frame);
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (wrapRef.current?.contains(target) || tooltipRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const handleReposition = () => updatePosition();

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, updatePosition]);

  const tooltip = open && typeof document !== "undefined"
    ? createPortal(
        <span
          ref={tooltipRef}
          id={tooltipIdRef.current}
          className="field-info-tooltip field-info-tooltip--portal is-visible"
          role="tooltip"
          style={{ left: position.left, top: position.top, width: position.width }}
        >
          <strong>Password Criteria</strong>
          <span>{PASSWORD_POLICY_MESSAGE}</span>
        </span>,
        document.body
      )
    : null;

  return (
    <span
      ref={wrapRef}
      className={`field-info-wrap${open ? " is-open" : ""}${className ? ` ${className}` : ""}`.trim()}
      onMouseEnter={() => setOpen(true)}
    >
      <button
        className="field-info-btn"
        type="button"
        aria-label="Password Criteria"
        aria-expanded={open}
        aria-describedby={open ? tooltipIdRef.current : undefined}
        onClick={() => setOpen((value) => !value)}
        onFocus={() => setOpen(true)}
        onBlur={(event) => {
          const related = event.relatedTarget;
          if (wrapRef.current?.contains(related) || tooltipRef.current?.contains(related)) return;
          setOpen(false);
        }}
      >
        <span className="field-info-letter" aria-hidden="true">i</span>
      </button>
      {tooltip}
    </span>
  );
}

export function normalizeChecklistHeaderLabel(label) {
  return String(label || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function checklistMonthParts(value) {
  const raw = String(value ?? "").trim();
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(raw);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (year < 1 || month < 1 || month > 12) return null;

  if (match[3] != null) {
    const day = Number(match[3]);
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const daysByMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day < 1 || day > daysByMonth[month - 1]) return null;
  }

  return { year: match[1], month: match[2] };
}

export function normalizeChecklistMonthForInput(value) {
  const parts = checklistMonthParts(value);
  return parts ? `${parts.year}-${parts.month}` : "";
}

export function formatChecklistMonth(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const parts = checklistMonthParts(raw);
  return parts ? `${parts.month}-${parts.year}` : raw;
}

export function isChecklistMonthLabel(label) {
  const key = normalizeChecklistHeaderLabel(label);
  return key === "mfgdate" || key === "expdateusebefore" || key === "expirydate";
}

export function getChecklistMonthHeaderValue(header, label) {
  const source = header && typeof header === "object" ? header : {};
  const direct = source[label] ?? source[label?.replace(/:\s*$/, " :")];
  if (direct != null && String(direct).trim() !== "") return direct;

  const labelKey = normalizeChecklistHeaderLabel(label);
  for (const [sourceLabel, value] of Object.entries(source)) {
    const sourceKey = normalizeChecklistHeaderLabel(sourceLabel);
    if (labelKey === "mfgdate" && sourceKey === "mfgdate") return value;
    if (
      ["expdateusebefore", "expirydate"].includes(labelKey)
      && ["expdateusebefore", "expirydate"].includes(sourceKey)
    ) {
      return value;
    }
  }
  return direct ?? "";
}


export function InlineFieldInfo({ text, label = "More information", className = "" }) {
  if (!text) return null;
  return (
    <span className={`field-info-wrap field-info-wrap--inline${className ? ` ${className}` : ""}`.trim()}>
      <button className="field-info-btn" type="button" aria-label={label} title={text}>
        <span className="field-info-letter" aria-hidden="true">i</span>
      </button>
    </span>
  );
}

export function isMrpHeaderLabel(label) {
  const key = normalizeChecklistHeaderLabel(label);
  return key === "mrp" || key === "mrprs" || key === "mrprupees" || key === "mrpinr" || key === "mrpinrs";
}

export function ChecklistHeaderTable({
  template,
  values,
  onChange,
  isDateField,
  isMonthField,
  isIntField,
  isFloatField,
  normalizeDecimal,
  lockedLabels = [],
  lockedHint = "Auto-filled from lot details",
  infoByLabel = {},
  highlightLabels = {},
  className = "",
}) {
  const pairs = template?.header?.pairs || [];
  const lockedSet = React.useMemo(
    () => new Set((lockedLabels || []).map((label) => normalizeChecklistHeaderLabel(label))),
    [lockedLabels],
  );
  const isLocked = React.useCallback(
    (label) => lockedSet.has(normalizeChecklistHeaderLabel(label)),
    [lockedSet],
  );

  return (
    <div className={`table--card checklist-header-card ${className}`.trim()}>
      <div className="table-scroll">
        <table className="table table--tight checklist-header-table">
          <tbody>
            {pairs.map((pair, rowIndex) => {
              const labels = pair.filter((label) => String(label || "").trim() !== "");
              if (!labels.length) return null;
              return (
                <tr key={`${labels.join("-")}-${rowIndex}`}>
                  {labels.map((label) => {
                    const locked = isLocked(label);
                    const labelKey = normalizeChecklistHeaderLabel(label);
                    const configuredType = Object.entries(template?.header?.types || {}).find(
                      ([typeLabel]) => normalizeChecklistHeaderLabel(typeLabel) === labelKey,
                    )?.[1];
                    const monthField = String(configuredType || "").toLowerCase() === "month"
                      || isMonthField?.(label)
                      || isChecklistMonthLabel(label);
                    const dateField = !monthField && (
                      String(configuredType || "").toLowerCase() === "date"
                      || isDateField?.(label)
                    );
                    const infoText = infoByLabel?.[label] || infoByLabel?.[labelKey] || "";
                    const highlighted = !!(highlightLabels?.[label] || highlightLabels?.[labelKey]);
                    return (
                      <React.Fragment key={label}>
                        <td className="checklist-field-label">
                          <span>{label}</span>
                          {infoText ? <InlineFieldInfo text={infoText} label={`${label} information`} /> : null}
                          <span className="required-mark">*</span>
                        </td>
                        <td className="checklist-field-input">
                          {monthField || dateField ? (
                            <PremiumDateInput
                              required
                              inputType={monthField ? "month" : "date"}
                              readOnly={locked}
                              aria-readonly={locked ? "true" : undefined}
                              title={locked ? lockedHint : undefined}
                              inputClassName={`${locked ? "checklist-locked-input" : ""} ${highlighted ? "checklist-threshold-violation" : ""}`.trim() || undefined}
                              value={monthField ? normalizeChecklistMonthForInput(values?.[label]) : (values?.[label] ?? "")}
                              pickerLabel={`Open ${label} ${monthField ? "month" : "date"} picker`}
                              onChange={(event) => {
                                if (locked) return;
                                onChange?.(label, event.target.value);
                              }}
                            />
                          ) : (
                            <input
                              required
                              readOnly={locked}
                              aria-readonly={locked ? "true" : undefined}
                              title={locked ? lockedHint : undefined}
                              className={`${locked ? "checklist-locked-input" : ""} ${highlighted ? "checklist-threshold-violation" : ""}`.trim() || undefined}
                              type="text"
                              inputMode={isIntField?.(label) ? "numeric" : undefined}
                              pattern={isIntField?.(label) ? "[0-9]*" : undefined}
                              value={values?.[label] ?? ""}
                              onChange={(event) => {
                                if (locked) return;
                                let nextValue = event.target.value;
                                if (isIntField?.(label)) nextValue = nextValue.replace(/[^\d]/g, "").slice(0, 9);
                                else if (isFloatField?.(label)) nextValue = normalizeDecimal?.(nextValue) || nextValue;
                                onChange?.(label, nextValue);
                              }}
                            />
                          )}
                          {locked ? <div className="helper-text">{lockedHint}</div> : null}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  {labels.length === 1 ? <><td /><td /></> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function CalendarSvgIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8 3v3M16 3v3M4 9h16" />
      <rect x="4" y="5" width="16" height="16" rx="2.4" />
      <path d="M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01" />
    </svg>
  );
}

function openDateInputPicker(inputRef) {
  const input = inputRef?.current;
  if (!input) return;
  try {
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
  } catch {
    // Fall through to focus/click for browsers that expose showPicker but block it.
  }
  input.focus?.();
  try {
    input.click?.();
  } catch {
    // Focusing the input is the safest fallback when programmatic click is blocked.
  }
}

function PremiumDateInput({
  value,
  onChange,
  inputType = "date",
  inputClassName = "",
  pickerLabel = "Open date picker",
  inputKey,
  wrapClassName = "",
  disabled = false,
  readOnly = false,
  ...props
}) {
  const inputRef = React.useRef(null);
  return (
    <span className={`premium-date-wrap ${wrapClassName}`.trim()}>
      <input
        {...props}
        key={inputKey}
        ref={inputRef}
        type={inputType}
        lang="en-GB"
        className={`premium-date-input ${inputClassName}`.trim()}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        onChange={onChange}
      />
      <button
        className="premium-date-button"
        type="button"
        aria-label={pickerLabel}
        onClick={() => openDateInputPicker(inputRef)}
        disabled={disabled || readOnly}
      >
        <CalendarSvgIcon />
      </button>
    </span>
  );
}


function isValidIsoDate(value) {
  if (!value) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value));
}

export function DateRangePicker({
  value = {},
  onChange,
  onApply,
  onClear,
  label = "Date range",
  fromLabel = "From",
  toLabel = "To",
  className = "",
}) {
  const [clearKey, setClearKey] = React.useState(0);
  const dateFrom = value.date_from || "";
  const dateTo = value.date_to || "";
  const invalidFormat = !isValidIsoDate(dateFrom) || !isValidIsoDate(dateTo);
  const invalidOrder = dateFrom && dateTo && dateTo < dateFrom;
  const invalid = invalidFormat || invalidOrder;
  const message = invalidFormat
    ? "Use YYYY-MM-DD dates."
    : invalidOrder
      ? "To date cannot be before From date."
      : "";

  const update = (key, nextValue) => {
    onChange?.({ date_from: dateFrom, date_to: dateTo, [key]: nextValue });
  };

  const clear = () => {
    const empty = { date_from: "", date_to: "" };
    setClearKey((current) => current + 1);
    onChange?.(empty);
    onClear?.(empty);
  };

  return (
    <div className={`date-range-picker ${className}`.trim()}>
      {label ? <div className="date-range-picker-label">{label}</div> : null}
      <div className="date-range-picker-fields">
        <label className="date-range-field">
          <span>{fromLabel}</span>
          <PremiumDateInput
            inputKey={`from-${clearKey}`}
            value={dateFrom}
            pickerLabel={`Open ${fromLabel} date picker`}
            onChange={(event) => update("date_from", event.target.value)}
          />
        </label>
        <label className="date-range-field">
          <span>{toLabel}</span>
          <PremiumDateInput
            inputKey={`to-${clearKey}`}
            value={dateTo}
            pickerLabel={`Open ${toLabel} date picker`}
            onChange={(event) => update("date_to", event.target.value)}
          />
        </label>
        <div className="date-range-picker-actions">
          {onApply ? <button className="btn btn-secondary" type="button" onClick={() => !invalid && onApply?.()} disabled={invalid}>Apply</button> : null}
          <button className="btn btn-secondary" type="button" onClick={clear}>Clear</button>
        </div>
      </div>
      {message ? <div className="helper-text error-text">{message}</div> : null}
    </div>
  );
}

function defaultRowId(row, index) {
  return String(row?.id ?? row?.material_id ?? row?.lot_no ?? row?.lot ?? index);
}

function cellValue(row, column) {
  if (typeof column.value === "function") return column.value(row);
  return row?.[column.key];
}

export function LotSelectionTable({
  rows = [],
  columns = [],
  selectionMode = "single",
  selected,
  onSelectionChange,
  onRowClick,
  onDisabledRowClick,
  getRowId = defaultRowId,
  disabledRow,
  showCheckboxes = true,
  showOrderBadges = false,
  searchPlaceholder = "Search lots...",
  hideSearch = false,
  showSelectAll = false,
  toolbarExtras = null,
  searchValue,
  onSearchChange,
  sortOptions,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  emptyText = "No rows found.",
  pageSize = 10,
  className = "",
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeSortOptions = React.useMemo(() => {
    const supplied = Array.isArray(sortOptions) ? sortOptions.filter((option) => option && option.key) : [];
    if (supplied.length) return supplied;
    return safeColumns
      .filter((column) => column.sortable !== false)
      .map((column) => ({ key: column.key, label: column.label || column.key, value: column.value }));
  }, [sortOptions, safeColumns]);
  const [internalQuery, setInternalQuery] = React.useState("");
  const [internalSortBy, setInternalSortBy] = React.useState(safeSortOptions[0]?.key || "");
  const [internalSortOrder, setInternalSortOrder] = React.useState("asc");

  const query = searchValue ?? internalQuery;
  const effectiveSortBy = sortBy ?? internalSortBy;
  const effectiveSortOrder = sortOrder ?? internalSortOrder;

  React.useEffect(() => {
    if (!effectiveSortBy && safeSortOptions[0]?.key) {
      if (sortBy === undefined) setInternalSortBy(safeSortOptions[0].key);
      else onSortByChange?.(safeSortOptions[0].key);
    }
  }, [effectiveSortBy, safeSortOptions, sortBy, onSortByChange]);

  const selectedIds = React.useMemo(() => {
    if (selectionMode === "multi") return new Set((Array.isArray(selected) ? selected : []).map(String));
    return new Set(selected ? [String(selected)] : []);
  }, [selected, selectionMode]);
  const selectedOrder = React.useMemo(() => (Array.isArray(selected) ? selected.map(String) : []), [selected]);

  const filteredRows = React.useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    let out = safeRows;
    if (q) {
      out = out.filter((row) => safeColumns.some((column) => String(cellValue(row, column) ?? "").toLowerCase().includes(q)));
    }
    const sortOption = safeSortOptions.find((option) => option.key === effectiveSortBy);
    const sortColumn = safeColumns.find((column) => column.key === effectiveSortBy);
    if (sortOption || sortColumn) {
      const valueFor = (row) => {
        if (typeof sortOption?.value === "function") return sortOption.value(row);
        if (typeof sortColumn?.value === "function") return sortColumn.value(row);
        return row?.[effectiveSortBy];
      };
      out = [...out].sort((a, b) => {
        const av = valueFor(a);
        const bv = valueFor(b);
        const an = Number(av);
        const bn = Number(bv);
        let cmp;
        if (Number.isFinite(an) && Number.isFinite(bn) && String(av).trim() !== "" && String(bv).trim() !== "") cmp = an - bn;
        else cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true, sensitivity: "base" });
        return effectiveSortOrder === "desc" ? -cmp : cmp;
      });
    }
    return out;
  }, [safeRows, safeColumns, safeSortOptions, query, effectiveSortBy, effectiveSortOrder]);

  const pager = usePagination(filteredRows, pageSize);

  const visibleSelectableIds = React.useMemo(() => {
    if (selectionMode !== "multi") return [];
    return filteredRows
      .map((row, index) => ({ row, id: String(getRowId(row, index)) }))
      .filter(({ row }) => !disabledRow?.(row))
      .map(({ id }) => id);
  }, [filteredRows, selectionMode, getRowId, disabledRow]);

  const allVisibleSelected = visibleSelectableIds.length > 0 && visibleSelectableIds.every((id) => selectedIds.has(id));

  const toggleVisibleSelection = () => {
    if (selectionMode !== "multi") return;
    const current = Array.isArray(selected) ? selected.map(String) : [];
    if (allVisibleSelected) {
      const visible = new Set(visibleSelectableIds);
      onSelectionChange?.(current.filter((id) => !visible.has(id)), null);
      return;
    }
    const next = [...current];
    visibleSelectableIds.forEach((id) => {
      if (!next.includes(id)) next.push(id);
    });
    onSelectionChange?.(next, null);
  };

  const handleSearchChange = (nextValue) => {
    if (onSearchChange) onSearchChange(nextValue);
    else setInternalQuery(nextValue);
  };
  const handleSortByChange = (nextValue) => {
    if (onSortByChange) onSortByChange(nextValue);
    else setInternalSortBy(nextValue);
  };
  const handleSortOrderChange = (nextValue) => {
    if (onSortOrderChange) onSortOrderChange(nextValue);
    else setInternalSortOrder(nextValue);
  };

  const selectRow = (row, index) => {
    const id = String(getRowId(row, index));
    if (disabledRow?.(row)) {
      onDisabledRowClick?.(row);
      return;
    }
    if (selectionMode === "none") {
      onRowClick?.(row);
      return;
    }
    if (selectionMode === "multi") {
      const current = Array.isArray(selected) ? selected.map(String) : [];
      const exists = current.includes(id);
      onSelectionChange?.(exists ? current.filter((value) => value !== id) : [...current, id], row);
    } else {
      onSelectionChange?.(id, row);
    }
    onRowClick?.(row);
  };

  return (
    <div className={`lot-selection-table ${className}`.trim()}>
      <div className="filters-bar lot-selection-toolbar">
        {!hideSearch ? (
          <label className="field-grow">
            <span>Search</span>
            <input value={query} onChange={(event) => handleSearchChange(event.target.value)} placeholder={searchPlaceholder} />
          </label>
        ) : null}
        <label>
          <span>Sort by</span>
          <select value={effectiveSortBy} onChange={(event) => handleSortByChange(event.target.value)}>
            {safeSortOptions.map((option) => (
              <option key={option.key} value={option.key}>{option.label || option.key}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Order</span>
          <select value={effectiveSortOrder} onChange={(event) => handleSortOrderChange(event.target.value)}>
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>
        {toolbarExtras}
        {showSelectAll && selectionMode === "multi" ? (
          <button
            className="btn btn-secondary lot-selection-select-all"
            type="button"
            onClick={toggleVisibleSelection}
            disabled={!visibleSelectableIds.length}
          >
            {allVisibleSelected ? "Deselect All Visible" : "Select All Visible"}
          </button>
        ) : null}
      </div>
      <div className="table--card">
        <div className="table-scroll">
          <table className="table table--dense table--fixed">
            <thead>
              <tr>
                {selectionMode === "multi" && showCheckboxes ? <th className="lot-selection-checkbox-col">Select</th> : null}
                {showOrderBadges ? <th className="lot-selection-order-col">Order</th> : null}
                {safeColumns.map((column) => (
                  <th key={column.key}>{column.label || column.key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pager.pageData.map((row, index) => {
                const absoluteIndex = pager.start + index;
                const id = String(getRowId(row, absoluteIndex));
                const disabled = !!disabledRow?.(row);
                const selectedRow = selectedIds.has(id);
                const orderIndex = selectedOrder.indexOf(id);
                const clickable = disabled ? !!onDisabledRowClick : (selectionMode !== "none" || !!onRowClick);
                return (
                  <tr
                    key={id}
                    className={`${selectedRow ? "is-selected" : ""} ${disabled ? "is-disabled" : ""}`.trim() || undefined}
                    onClick={clickable ? () => selectRow(row, absoluteIndex) : undefined}
                    style={clickable ? { cursor: disabled ? "not-allowed" : "pointer" } : undefined}
                  >
                    {selectionMode === "multi" && showCheckboxes ? (
                      <td onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedRow}
                          disabled={disabled}
                          onChange={() => selectRow(row, absoluteIndex)}
                        />
                      </td>
                    ) : null}
                    {showOrderBadges ? (
                      <td>
                        {orderIndex >= 0 ? <span className="selection-order-badge">{orderIndex + 1}</span> : "—"}
                      </td>
                    ) : null}
                    {safeColumns.map((column) => (
                      <td key={column.key}>
                        {column.render
                          ? column.render(row, {
                              index,
                              absoluteIndex,
                              page: pager.page,
                              pageSize: pager.size,
                            })
                          : (cellValue(row, column) ?? "—")}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {filteredRows.length === 0 ? <tr><td colSpan={safeColumns.length + (selectionMode === "multi" && showCheckboxes ? 1 : 0) + (showOrderBadges ? 1 : 0)}>{emptyText}</td></tr> : null}
            </tbody>
          </table>
        </div>
        <Pager {...pager} />
      </div>
    </div>
  );
}

export function MaterialTemplateViewer({ specs = [], emptyText = "No specification rows found." }) {
  const rows = Array.isArray(specs) ? specs : [];
  const get = (row, keys) => {
    for (const key of keys) {
      const value = row?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") return value;
    }
    return "—";
  };
  return (
    <div className="table--card material-template-viewer">
      <div className="table-scroll">
        <table className="table table--tight table--fixed material-spec-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Units</th>
              <th>LL</th>
              <th>UL</th>
              <th>Data type</th>
              <th>Caution</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{get(row, ["Parameter", "parameter"])}</td>
                <td>{get(row, ["Units", "units", "Unit"])}</td>
                <td>{get(row, ["LL", "Lower Limit", "Lower limit", "lower_limit"])}</td>
                <td>{get(row, ["UL", "Upper Limit", "Upper limit", "upper_limit"])}</td>
                <td>{get(row, ["Data type", "Data Type", "data_type"])}</td>
                <td>{get(row, ["Caution", "caution"])}</td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6}>{emptyText}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export function usePagination(data, initialSize = 10) {
  const arr = Array.isArray(data) ? data : [];
  const [page, setPage] = React.useState(1);
  const [size, setSize] = React.useState(initialSize);
  const pageCount = Math.max(1, Math.ceil(arr.length / size));

  React.useEffect(() => {
    setPage((prev) => Math.min(prev, pageCount));
  }, [pageCount]);

  React.useEffect(() => {
    setPage(1);
  }, [size, arr.length]);

  const start = (page - 1) * size;
  const end = start + size;
  const pageData = React.useMemo(() => arr.slice(start, end), [arr, start, end]);
  const canPrev = page > 1;
  const canNext = page < pageCount;
  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(pageCount, p + 1));

  return { page, setPage, size, setSize, pageCount, pageData, canPrev, canNext, prev, next, total: arr.length, start };
}

export function Pager({ page, pageCount, size, setSize, prev, next, canPrev, canNext, total = 0, start = 0 }) {
  return (
    <div className="pager">
      <div className="pager-meta">
        <span>Showing {total ? start + 1 : 0}–{Math.min(start + size, total)} of {total}</span>
        <label>
          <span>Rows</span>
          <select value={size} onChange={(e) => setSize(parseInt(e.target.value || "10", 10))}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
      </div>
      <div className="pager-controls">
        <button className="btn btn-secondary" disabled={!canPrev} onClick={prev} type="button">Prev</button>
        <span className="pager-page">Page {page} of {pageCount}</span>
        <button className="btn btn-secondary" disabled={!canNext} onClick={next} type="button">Next</button>
      </div>
    </div>
  );
}
